import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import {
  Button, Grid, TextField,
  Typography,
  SvgIcon, InputAdornment, Autocomplete, Divider,
  Box, Tooltip, useTheme, Tabs, Tab, Dialog,
  DialogActions, DialogContent, DialogTitle,
  MenuItem,
  Skeleton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';

export type SaveStatus = 'standard' | 'idle' | 'saving' | 'saved' | 'error';
import { SearchSm } from '../../../assets';
import { OrderTypeTranslationMap } from '../../../enums/OrderType';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../../../components/shared/useUser';
import { client, SimpleCategory, OrderDetails, OrderItemDetails, DeleteOrderItemsRequest, OrderlySystem, ItemDocument, ContainerFillResult, OrderItemUpdate, DeliveryOptions } from '../../../app/services/api/orderManagementClient';
import dayjs, { Dayjs } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import CopyOrderModal from '../../../components/shared/orders/CopyOrderModal';
import { ConfirmationDialog } from '../../../components/shared/dialogs/ConfirmationDialog';
import { OrderStatus } from '../../../enums/OrderStatus';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import { exportFile } from '../../../utils';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import BackButton from '../../../components/shared/actions/BackButton';
import { useTranslation } from 'react-i18next';
import { isOrderCategory, OrderCategoriesTranslationMap } from '../../../enums/OrderCategories';
import './OrderDetails.css';
import { QuantityWarningDialog } from '../../../components/shared/orders/QuantityWarningDialog';
import OrderAuditModal from '../../../components/shared/orders/OrderAuditModal';
import ContainerPlanningView from './ContainerPlanningView';
import useLogError from '../../../hooks/useLogError';
import OrderStatusChip from '../../../components/shared/orders/OrderStatusChip';
import useNswagClient from '../../../hooks/api/useNswagClient';
import OrderDocuments from '../OrderDetailsOfd/OrderDocuments';
import { downloadFile } from '../ProductDetails/utils/ProductDocumentUtils';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ActionConfirmationDialog } from '../../../components/shared/dialogs/ActionConfirmationDialog';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import { useActiveOrder } from '../../../contexts/ActiveOrderContext';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import ItemPriceOverrideModal from './modals/ItemPriceOverrideModal';
import { OnboardingPageData, Int32StringKeyValuePair } from '../../../app/services/api/ofdClient';
import OrderTable from './OrderTable';
import axios, { CancelTokenSource } from 'axios';
import { set } from 'lodash';
import { TaskType } from '../../../enums/TaskType';
import { Roles } from '../../../app/services/api/generated';
import useDeliveryPricing from '../../../hooks/useDeliveryPricing';

interface DialogState {
  open: boolean;
  title: string;
  message: string;
  confirmAction: () => void;
}

const enum OrderDetailsEnum {
  Basket = 1,
  Shipping = 2,
  Documents = 3,
}

const isPromoOrderItem = (item: Pick<OrderItemDetails, 'promotionAllocationId'>) => item.promotionAllocationId != null;

const OrderDetailsPageOfd = () => {
  dayjs.extend(duration);
  const theme = useTheme();
  const navigate = useNavigate();
  const { logError } = useLogError();
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const { t } = useTranslation('orderDetails');
  const { setActiveOrder, setContainerData: setActiveContainerData, setShippingDetails, clearActiveOrder } = useActiveOrder();
  const {
    getOnboardingPageData,
    addTasks,
    downloadProductDocument,
    getStoreUsersWithRole,
  } = useNswagClient();
  const { resolveDeliveryCharge } = useDeliveryPricing({ storeNumber: selectedStore?.storeNumber });

  const [loading, setLoading] = useState(true);
  const [orderType, setOrderType] = useState<string>('');

  const [productSearch, setProductSearch] = useState('');
  const [category, setCategory] = useState<number>(-1);

  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>();
  const [filteredItems, setFilteredItems] = useState<OrderItemDetails[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const [categories, setCategories] = useState<SimpleCategory[]>([]);
  const quantitiesRef = useRef<{ [webOrderItemId: number]: number }>({});
  const promoQuantitiesRef = useRef<{ [webOrderItemId: number]: number }>({});
  const [isCopyOrderModalOpen, setIsCopyOrderModalOpen] = useState(false);
  const [isOrderAuditModalOpen, setIsOrderAuditModalOpen] = useState(false);

  const modifiedItemsRef = useRef<Set<number>>(new Set());
  const orderDetailsForTableRef = useRef<OrderDetails | null>(null);
  const autosaveCancelSourceRef = useRef<CancelTokenSource | null>(null);

  const [deliveryMethod, setDeliveryMethod] = useState<string>('Ex Works');
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOptions[]>([]);
  const [resolvedDeliveryOptionCharges, setResolvedDeliveryOptionCharges] = useState<Map<number, number>>(new Map());
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState<string>('');
  const [containerCharge, setContainerCharge] = useState<string>('0.00');
  const [deliveryChargeTotal, setDeliveryChargeTotal] = useState<string>(''); 
  const [isAutomatedPricingActive, setIsAutomatedPricingActive] = useState<boolean>(false);

  const [country, setCountry] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [containerType, setContainerType] = useState<number | string>('');
  const [palletType, setPalletType] = useState<number | string>('');

  const [navigateToIndex, setNavigateToIndex] = useState<boolean>(false);
  const [isExitAndSaveClicked, setIsExitAndSaveClicked] = useState<boolean>(false);

  const [showQuantityWarningDialog, setShowQuantityWarningDialog] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');

  const [productDocuments, setProductDocuments] = useState<(ItemDocument & { productId: number, name: string; sku: string })[]>([]);
  const [containerData, setContainerData] = useState<ContainerFillResult | null>(null); const [selectedDeliveryDate, setSelectedDeliveryDate] = useState<Dayjs | null>(null);
  const [customerPoNumber, setCustomerPoNumber] = useState<string>('');
  const [customerPoNumberError, setCustomerPoNumberError] = useState<boolean>(false);

  const [isDownloadingSpreadsheet, setIsDownloadingSpreadsheet] = useState<boolean>(false);
  const [isUploadingSpreadsheet, setIsUploadingSpreadsheet] = useState<boolean>(false);
  const [handleShowOverridePrice, setHandleShowOverridePrice] = useState<boolean>(false);
  const [selectedOverrideItem, setSelectedOverrideItem] = useState<OrderItemDetails | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [containerSizes, setContainerSizes] = useState<Int32StringKeyValuePair[]>([]);

  const [tabValue, setTabValue] = useState<OrderDetailsEnum>(OrderDetailsEnum.Basket);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    title: '',
    message: '',
    confirmAction: () => { },
  });

  const [isCheckoutDialogOpen, setIsCheckoutDialogOpen] = useState(false);

  const [checkoutDialogData, setCheckoutDialogData] = useState({
    title: '',
    message: '',
    confirmAction: () => { },
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const { id } = useParams();

  const blocker = useBlocker(() => modifiedItemsRef.current.size > 0 && !isExitAndSaveClicked);

  const canCheckoutOrder = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending &&
      (orderDetails?.items?.length ?? 0) > 0;
  };

  const [rejectDialogOpen, setRejectDialogOpen] = useState<boolean>(false);

  const canEditOrder = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending ||
      orderDetails?.status === OrderStatus.Review ||
      orderDetails?.status === OrderStatus.Amended;
  };

  const loadContainerSizes = async () => {
    getOnboardingPageData().then((result: OnboardingPageData) => {
      if (result) {
        setContainerSizes(result.containerSizes ?? []);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }).catch((result: any) => {
      console.error(result);
    });
  };

  const handleEditClick = (item: OrderItemDetails) => {
    setHandleShowOverridePrice(true);
    setSelectedOverrideItem(item);
  };

  const canChangeOrderQuantity = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending || canApproveOrder();
  };

  const canDeleteOrderItem = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending || canApproveOrder();
  };

  const canChangeContainer = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending || canApproveOrder();
  };

  const canChangePalletType = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending || canApproveOrder();
  };

  const canApproveOrder = (): boolean => {
    return hasPermissionTo(['OrdersApprove']) && orderDetails?.status === OrderStatus.Review;
  };

  const canApproveAmendedOrder = (): boolean => {
    return !orderDetails?.skipOrderReview &&
      hasPermissionTo(['OrdersApproveConfirmation']) &&
      orderDetails?.status === OrderStatus.Amended;
  };

  const canRejectAmendedOrder = (): boolean => {
    return !orderDetails?.skipOrderReview &&
      hasPermissionTo(['OrdersRejectConfirmation']) &&
      orderDetails?.status === OrderStatus.Amended;
  };

  const isPendingEditableForSkipReview = (): boolean => {
    return !!orderDetails?.skipOrderReview && orderDetails?.status === OrderStatus.Pending;
  };

  const isCustomerPoNumberRequired = (): boolean => {
    return !!orderDetails?.requireCustomerPoNumber;
  };

  const hasValidCustomerPoNumber = (): boolean => {
    if (!isCustomerPoNumberRequired()) {
      return true;
    }

    return customerPoNumber.trim().length > 0;
  };

  const ensureCustomerPoNumberIsValid = (): boolean => {
    if (!hasValidCustomerPoNumber()) {
      setCustomerPoNumberError(true);
      handleSnackbarOpen(t('customerPoNumberRequired'), 'error');
      return false;
    }

    setCustomerPoNumberError(false);
    return true;
  };

  const displayDeliveryDate = (): boolean => {
    return orderDetails?.status === OrderStatus.Review ||
      orderDetails?.status === OrderStatus.Amended ||
      isPendingEditableForSkipReview();
  };

  const displayCustomerPoNumber = (): boolean => {
    return displayDeliveryDate() ||
      (isCustomerPoNumberRequired() && orderDetails?.status === OrderStatus.Pending);
  };

  const canAmendOrderDate = (): boolean => {
    return hasPermissionTo(['OrdersChangeDeliveryDate']) &&
      (orderDetails?.status === OrderStatus.Review || isPendingEditableForSkipReview());
  };

  const getMinimumDeliveryDate = (): Dayjs => {
    if (orderDetails?.minimumDeliveryDate) {
      const parsed = dayjs(orderDetails.minimumDeliveryDate, 'DD-MM-YYYY');
      if (parsed.isValid()) {
        return parsed.startOf('day');
      }
    }
    return dayjs().startOf('day');
  };

  const canAmendCustomerPoNumber = (): boolean => {
    if (isCustomerPoNumberRequired() && orderDetails?.status === OrderStatus.Pending) {
      return canEditOrder();
    }

    return hasPermissionTo(['OrdersChangeCustomerPoNumber']) &&
      (orderDetails?.status === OrderStatus.Review || isPendingEditableForSkipReview());
  };

  const canDeleteOrder = (): boolean => {
    return orderDetails?.status === OrderStatus.Pending;
  };

  const isOrderEditableByStatus = (order: OrderDetails | null | undefined): boolean => {
    return order?.status === OrderStatus.Pending ||
      (hasPermissionTo(['OrdersApprove']) && order?.status === OrderStatus.Review);
  };

  const canUpdateDeliveryMethod = (): boolean => {
    return orderDetails?.status === OrderStatus.Review ||
      (hasPermissionTo(['DeliveryMethodWrite']) && orderDetails?.status === OrderStatus.Pending);
  };
  const canUpdateDeliveryCharge = (): boolean => {
    return orderDetails?.status === OrderStatus.Review ||
      (hasPermissionTo(['DeliveryChargeWrite']) && orderDetails?.status === OrderStatus.Pending);
  };
  const isOrderEditable = (): boolean => {
    return isOrderEditableByStatus(orderDetails);
  };

  useEffect(() => {
    getCurrencyCodeConfiguration();
    loadContainerSizes();
  }, []);

  useEffect(() => {
    if (orderDetails) {
      const categoriesById: Record<number, SimpleCategory> = {};
      orderDetails.items.forEach((item) => {
        if (!categoriesById[item.categoryId]) {
          categoriesById[item.categoryId] = { id: item.categoryId, name: item.category };
        }
      });
      setCategories(Object.values(categoriesById));
      if (orderDetails.deliveryDate) {
        const parsedDate = dayjs(orderDetails.deliveryDate, 'DD-MM-YYYY');
        if (parsedDate.isValid()) {
          setSelectedDeliveryDate(parsedDate);
        }
      } else {
        setSelectedDeliveryDate(null);
      }
      setCustomerPoNumber(orderDetails.customerPoNumber ?? '');
      setCustomerPoNumberError(false);
    }
  }, [orderDetails]);

  useEffect(() => {
    const beforeUnload = (event: { preventDefault: () => void; returnValue: string; }) => {
      if (modifiedItemsRef.current.size > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [modifiedItemsRef.current]);

  useEffect(() => {
    if (navigateToIndex) {
      navigate('/store/orders');
      if (blocker?.state === 'blocked') {
        blocker.proceed();
      }
      setNavigateToIndex(false);
    }
  }, [navigateToIndex, blocker]);

  // Auto-save when blocker is triggered
  useEffect(() => {
    const handleBlocker = async () => {
      if (blocker.state === 'blocked' && modifiedItemsRef.current.size > 0) {
        autosaveItem();
        blocker.proceed?.();
      }
    };
    handleBlocker();
  }, [blocker.state]);

  useEffect(() => {
    const fetchShippingDetails = async () => {
      try {

        const result = await client.getShippingDetails(
          selectedStore?.storeNumber ?? '',
          orderDetails?.id?.toString() ?? '',
          selectedShippingMethodId ?? '',
        );

        if (result.success && result.data) {
          const data = result.data;
          setDeliveryMethod(data.deliveryMethod || 'Ex Works');
          setCountry(data.country);
          setLocation(data.location);
          setContainerType(data.containerType ?? '');
          setPalletType(data.palletType ?? '');
          setDeliveryOptions(data.deliveryOptions || []);
          setContainerCharge((data.deliveryCharge || 0).toString());
          // A brand new order has no items/pallets yet — the API's returned totalDeliveryCharge reflects
          // the default shipping method's flat rate, not an actual computed charge. Force £0 until there's
          // something in the order to actually charge for; the recalculate effect below takes over once
          // containerData reflects real pallets.
          const hasItems = (orderDetails?.items?.length ?? 0) > 0;
          const initialChargeTotal = hasItems ? (data.totalDeliveryCharge || 0).toString() : '0.00';
          setDeliveryChargeTotal(initialChargeTotal);
          // Keep the ActiveOrder widget (reads from ActiveOrderContext, not this page's local state)
          // in sync from the start — otherwise it shows a stale/incorrect total until the recalculate
          // effect below fires, which never happens for an order with no containerData yet.
          updateShippingDetailsContextForActiveOrder(
            data.deliveryMethod || 'Ex Works',
            (data.deliveryCharge || 0).toString(),
            initialChargeTotal,
          );

          if (data.deliveryMethodId) {
            setSelectedShippingMethodId(data.deliveryMethodId);
          } else if (data.deliveryOptions && data.deliveryOptions.length > 0) {
            const matchingOption = data.deliveryOptions.find(option =>
              option.name === data.deliveryMethod,
            );
            if (matchingOption) {
              setSelectedShippingMethodId(matchingOption.shippingMethodId.toString());
            }
          }
        }
      }
      catch (error) {
        console.error('Failed to fetch shipping details', error);
      } finally {
        shippingDataLoaded.current = true;
      }
    };

    if (selectedStore?.storeNumber && orderDetails?.id) {
      fetchShippingDetails();
    }
  }, [selectedStore?.storeNumber, orderDetails?.id]);

  useEffect(() => {
    if (containerData && containerCharge) {
      let active = true;

      const recalculate = async () => {
        let containerChargeValue = parseFloat(containerCharge) || 0;
        let total: number;

        // Re-resolve pricing engine when pallet count changes
        if (containerData.totalPalletsNeeded > 0 && selectedShippingMethodId) {
          const selectedOption = deliveryOptions.find(
            o => o.shippingMethodId.toString() === selectedShippingMethodId,
          );
          const engineResult = await resolveDeliveryCharge(
            Number(selectedShippingMethodId),
            containerData.totalPalletsNeeded,
            containerData.containerFillPercents,
            orderDetails?.deliveryDate ?? new Date().toISOString().split('T')[0],
            selectedOption?.charge,
          );
          if (engineResult) {
            if (!active) {
              return;
            }
            // Engine returns total order charge — derive per-container value
            total = engineResult.totalCharge ?? 0;
            const count = computeContainerCount(containerData);
            containerChargeValue = count > 0 ? total / count : total;
            setContainerCharge(containerChargeValue.toString());
            setIsAutomatedPricingActive(true);
          } else {
            // Fallback: flat rate × container count
            const count = computeContainerCount(containerData);
            total = containerChargeValue * count;
            setIsAutomatedPricingActive(false);
          }
        } else {
          const count = computeContainerCount(containerData);
          total = containerChargeValue * count;
          setIsAutomatedPricingActive(false);
        }

        if (!active) {
          return;
        }

        setDeliveryChargeTotal(total.toString());
        updateShippingDetailsContextForActiveOrder(deliveryMethod, containerChargeValue.toFixed(2), total.toFixed(2));
        if (shippingDataLoaded.current) {
          autosaveShippingDetails(selectedShippingMethodId, total, undefined, undefined, containerChargeValue);
        }
      };
      recalculate();

      return () => {
        active = false;
      };
    }
  }, [
    containerData?.containerFillPercents,
    containerData?.totalContainersUsed,
    containerData?.totalPalletsNeeded,
    deliveryOptions,
    deliveryMethod,
    orderDetails?.deliveryDate,
    resolveDeliveryCharge,
    selectedShippingMethodId,
  ]);

  useEffect(() => {
    let active = true;

    const resolveOptionCharges = async () => {
      if (!containerData || containerData.totalPalletsNeeded <= 0 || deliveryOptions.length === 0) {
        setResolvedDeliveryOptionCharges(new Map());
        return;
      }

      const count = computeContainerCount(containerData);
      const deliveryDate = orderDetails?.deliveryDate ?? new Date().toISOString().split('T')[0];

      const entries = await Promise.all(deliveryOptions.map(async (option) => {
        const engineResult = await resolveDeliveryCharge(
          Number(option.shippingMethodId),
          containerData.totalPalletsNeeded,
          containerData.containerFillPercents,
          deliveryDate,
          option.charge,
        );

        if (!engineResult) {
          return [Number(option.shippingMethodId), option.charge] as const;
        }

        const totalCharge = engineResult.totalCharge ?? 0;
        const perContainerCharge = count > 0 ? totalCharge / count : totalCharge;
        return [Number(option.shippingMethodId), perContainerCharge] as const;
      }));

      if (active) {
        setResolvedDeliveryOptionCharges(new Map(entries));
      }
    };

    resolveOptionCharges();

    return () => {
      active = false;
    };
  }, [
    containerData?.containerFillPercents,
    containerData?.totalContainersUsed,
    containerData?.totalPalletsNeeded,
    deliveryOptions,
    orderDetails?.deliveryDate,
    resolveDeliveryCharge,
  ]);

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const lastShippingAutosaveSignatureRef = useRef<string>('');

  const computeContainerCount = (d: ContainerFillResult | null): number => (
    // Do not floor to 1 — a brand new/empty order genuinely has 0 containers, and every call site
    // that multiplies by this count needs that 0 to actually zero out the charge. Call sites that
    // divide by this count already guard against 0 explicitly (count > 0 ? total / count : total).
    d?.totalContainersUsed ?? 0
  );

  const shippingDataLoaded = useRef(false);

  const autosaveShippingDetails = (
    shippingMethodId?: string,
    deliveryMethodCharge?: number,
    newContainerType?: string | number,
    newPalletType?: string | number,
    deliveryChargeValue?: number,
  ) => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    debounceTimeout.current = setTimeout(async () => {
      try {
        const resolvedShippingMethodId = shippingMethodId ?? selectedShippingMethodId;
        const resolvedDeliveryCharge = deliveryChargeValue ?? (parseFloat(containerCharge) || 0);
        const totalCharge = deliveryMethodCharge ?? parseFloat(deliveryChargeTotal || '0');
        const resolvedContainerType = Number(newContainerType ?? containerType);
        const resolvedPalletType = Number(newPalletType ?? palletType);

        const autosaveSignature = [
          selectedStore?.storeNumber ?? '',
          orderDetails?.id ?? '',
          resolvedShippingMethodId,
          deliveryMethod,
          resolvedDeliveryCharge.toFixed(4),
          totalCharge.toFixed(4),
          resolvedContainerType,
          resolvedPalletType,
        ].join('|');

        if (autosaveSignature === lastShippingAutosaveSignatureRef.current) {
          return;
        }
        lastShippingAutosaveSignatureRef.current = autosaveSignature;

        const response = await client.setShippingDetails(
          selectedStore?.storeNumber ?? '',
          orderDetails?.id.toString() ?? '',
          {
            deliveryCharge: resolvedDeliveryCharge,
            deliveryMethod:  deliveryMethod,
            deliveryMethodId: resolvedShippingMethodId,
            orderStatus: orderDetails?.statusId.toString() ?? '',
            deliveryOptions: deliveryOptions,
            country,
            location,
            containerType: resolvedContainerType,
            palletType: resolvedPalletType,
            totalDeliveryCharge: totalCharge,
          },
        );
        if (response.success) {
          handleSnackbarOpen(('Shipping details updated successfully.'), 'success');
          
        }
        else {
          setWarningMessage(response.message);
        }
        await fetchOrderDetails();
      } catch (error) {
        console.error('Failed to autosave shipping details', error);
      }
    }, 500);
  };

  const updateItemPriceOverride = async (editedItem: OrderItemUpdate) => {
    if (!orderDetails) return;

    const selectedOrderItemId = selectedOverrideItem?.id;

    // keep all items, update the one that matches
    const updatedItems = orderDetails.items.map(item =>
      item.id === selectedOrderItemId
        ? {
          ...item,
          id: item.itemId,
          quantity: editedItem.quantity,
          overridePrice: editedItem.overridePrice,
          overrideReason: editedItem.overrideReason,
          ...(isPromoOrderItem(item) ? { isPromoLine: true as const } : {}),
        }
        : {
          ...item,
          id: item.itemId,
          ...(isPromoOrderItem(item) ? { isPromoLine: true as const } : {}),
        },
    );


    const updatedOrder = {
      ...orderDetails,
      items: updatedItems,
      orderlySystem: OrderlySystem.OFD,
    };


    try {
      const result = await client.updateOrder(selectedStore?.storeNumber, updatedOrder);
      if (result.success) {
        handleSnackbarOpen(t('itemUpdated'), 'success');
        await fetchOrderDetails();
      }
    } catch (error) {
      handleSnackbarOpen(t('updateError') + ' ' + error, 'error');
    }

    setHandleShowOverridePrice(false);
    setSelectedOverrideItem(undefined);
  };

  const autosaveItem = async (setSaveStatus?: (status: SaveStatus) => void) => {
    if (!orderDetails) return;

    // Set status to saving
    setSaveStatus?.('saving');

    // Cancel any in-flight autosave
    if (autosaveCancelSourceRef.current) {
      autosaveCancelSourceRef.current.cancel('Autosave superseded by a new change');
    }
    const cancelSource = axios.CancelToken.source();
    autosaveCancelSourceRef.current = cancelSource;

    // Use latest quantities (state) to build payload
    const normalUpdatedItems = orderDetails.items
      .filter(item => !isPromoOrderItem(item) && quantitiesRef.current[item.id] !== undefined)
      .map(item => ({
        id: item.itemId,
        quantity: quantitiesRef.current[item.id] ?? item.quantity,
      }));
    const promoUpdatedItems = orderDetails.items
      .filter(item => isPromoOrderItem(item) && promoQuantitiesRef.current[item.id] !== undefined)
      .map(item => ({
        id: item.itemId,
        quantity: promoQuantitiesRef.current[item.id] ?? item.quantity,
        isPromoLine: true as const,
      }));
    const updatedItems = [...normalUpdatedItems, ...promoUpdatedItems];

    const updatedOrder = {
      id: orderDetails.id,
      items: updatedItems,
      orderlySystem: OrderlySystem.OFD,
    };

    try {
      const result = await client.updateOrder(selectedStore?.storeNumber, updatedOrder, cancelSource.token);

      // If this request was cancelled, ignore the result
      if (cancelSource.token.reason) {
        return;
      }

      if (result.success) {
        const baseOrder =
          orderDetailsForTableRef.current ?? orderDetails;

        const updatedOrderForTable: OrderDetails = {
          ...baseOrder,
          status: baseOrder.status === OrderStatus.Review ? OrderStatus.Amended : baseOrder.status,
          items: baseOrder.items.map(item => {
            const isPromo = isPromoOrderItem(item);
            const newQty = isPromo
              ? (promoQuantitiesRef.current[item.id] ?? item.quantity)
              : (quantitiesRef.current[item.id] ?? item.quantity);
            // Adjust remaining promo capacity optimistically so the tooltip stays accurate
            // without a round-trip. remainingQty = cap - sum(allocations), so delta in this
            // allocation shifts it by the same amount in the opposite direction.
            const qtyDelta = isPromo ? newQty - item.quantity : 0;
            return {
              ...item,
              quantity: newQty,
              promotionRemainingQuantity:
                isPromo && item.promotionRemainingQuantity != null
                  ? item.promotionRemainingQuantity - qtyDelta
                  : item.promotionRemainingQuantity,
            };
          }),
        };

        // Update the ref the table reads from
        orderDetailsForTableRef.current = updatedOrderForTable;
        setOrderDetails(updatedOrderForTable);

        if (updatedOrderForTable.status === OrderStatus.Amended) {
          setActiveOrder(null);
        }

        // Sync filteredItems state so promoLine (used for tooltip + QuantityCell initialQuantity)
        // reflects the updated quantity and promotionRemainingQuantity immediately.
        // Preserves any active filter — only items present in filteredItems are replaced.
        const updatedItemLookup = new Map(updatedOrderForTable.items.map(i => [i.id, i]));
        setFilteredItems(prev => prev.map(item => updatedItemLookup.get(item.id) ?? item));

        // All pending changes in both refs were persisted in this autosave cycle.
        modifiedItemsRef.current = new Set();

        quantitiesRef.current = {};
        promoQuantitiesRef.current = {};

        if (selectedStore?.storeNumber && orderDetails.id) {
          try {
            const containerResult = await client.getStoreContainerData(
              selectedStore.storeNumber,
              orderDetails.id.toString(),
            );
            setContainerData(containerResult.data);
            setActiveContainerData(containerResult.data);
          } catch (err) {
            logError(err);
          }
        }

        // Set status to saved, then clear after delay
        setSaveStatus?.('saved');
        setTimeout(() => {
          setSaveStatus?.('standard');
        }, 3000);
      } else {
        setSaveStatus?.('error');
        handleSnackbarOpen(result.message || t('updateError'), 'error');
        setTimeout(() => setSaveStatus?.('standard'), 5000);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        // Request was cancelled; silently ignore
        console.log('autosaveItem cancelled:', error.message);
        return;
      }
      setSaveStatus?.('error');
      setTimeout(() => setSaveStatus?.('standard'), 5000);
      handleSnackbarOpen(t('updateError') + ' ' + error, 'error');
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedStore, id]);

  const loadData = async () => {
    setLoading(true);
    await fetchOrderDetails();
    setLoading(false);
  };

  useEffect(() => {
    if (orderDetails && (Object.keys(quantitiesRef.current).length > 0 || Object.keys(promoQuantitiesRef.current).length > 0)) {
      const updatedOrder = {
        ...orderDetails,
        items: orderDetails.items.map(item => ({
          ...item,
          quantity: isPromoOrderItem(item)
            ? (promoQuantitiesRef.current[item.id] ?? item.quantity)
            : (quantitiesRef.current[item.id] ?? item.quantity),
        })),
      };
      if (isOrderEditable()) {
        setActiveOrder(updatedOrder);
      }
    }
  }, [quantitiesRef.current, promoQuantitiesRef.current, orderDetails, setActiveOrder]);

  useEffect(() => {
    if (orderDetails && filteredItems.length > 0) {
      fetchItemDocuments();
    }
  }, [orderDetails, filteredItems]);

  const fetchOrderDetails = async () => {
    try {
      const result = await client.getOrder(selectedStore?.storeNumber, parseInt(id ?? ''));
      setOrderDetails(result.data);
      setFilteredItems(result.data.items);
      setOrderType(result.data.orderType);

      orderDetailsForTableRef.current = result.data;

      if (isOrderEditableByStatus(result.data)) {
        setActiveOrder(result.data);
      } else {
        setActiveOrder(null);
      }

      if (selectedStore?.storeNumber && result.data.id) {
        try {
          const containerResult = await client.getStoreContainerData(
            selectedStore.storeNumber,
            result.data.id.toString(),
          );
          setContainerData(containerResult.data);
          setActiveContainerData(containerResult.data);

          if (containerResult.data.errors && containerResult.data.errors.length > 0) {
            handleSnackbarOpen(t('fetchingErrorMessage') + ' ' + containerResult.data.errors.join(', '), 'error');
          }
        } catch (err) {
          logError(err);
          handleSnackbarOpen(t('fetchingErrorMessage') + ' ' + err, 'error');
        }
      }

    } catch (error) {
      handleSnackbarOpen(t('fetchingErrorMessage') + ' ' + error, 'error');
    }
  };

  const fetchItemDocuments = async () => {
    try {
      if (orderDetails && orderDetails.id) {
        const response = await client.getOrderDocuments(selectedStore?.storeNumber, orderDetails.id);
        if (response.success) {
          const allDocs: (ItemDocument & { productId: number; name: string; sku: string })[] = [];
          Object.entries(response.data).forEach(([itemId, documents]) => {
            const orderItem = filteredItems.find(item => item.itemId === parseInt(itemId));
            if (orderItem) {
              const docsWithProduct = documents.map(doc => ({
                ...doc,
                productId: orderItem.itemId,
                name: orderItem.name,
                sku: orderItem.sku,
              }));
              allDocs.push(...docsWithProduct);
            }
          });
          setProductDocuments(allDocs);
        } else {
          console.error('Failed to fetch order documents:', response.message);
        }
      }
    } catch (error) {
      console.error('Error fetching order documents:', error);
      handleSnackbarOpen(t('fetchingErrorMessage') + ' ' + error, 'error');
    }
  };

  const handleOpenDialogForClearBasket = (orderId: number) => {
    setDialogState({
      open: true,
      title: t('clearBasketModalTitle'),
      message: t('clearBasketModalMessage'),
      confirmAction: () => onClearBasket(orderId),
    });
  };

  const onClearBasket = async (orderId: number) => {
    try {
      setLoading(true);
      const response = await client.deleteAllOrderItems(selectedStore?.storeNumber, orderId.toString());
      if (response.success) {
        handleSnackbarOpen(t('basketCleared'), 'success');
        await fetchOrderDetails();
      }
      else {
        setWarningMessage(response.message);
      }
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
    }

  };

  const handleSearch = () => {
    if (orderDetails) {
      const filtered = orderDetails.items.filter(item => {
        const matchesSearchText = productSearch === '' || item.sku.toLowerCase().includes(productSearch.toLowerCase()) || item.name.toLowerCase().includes(productSearch.toLowerCase());
        const matchesCategory = category === -1 || item.categoryId === category;
        return matchesSearchText && matchesCategory;
      });
      setFilteredItems(filtered);
    }
  };

  const handleDeleteOrderItem = async (orderId: number, orderItemId: number) => {
    if (!orderId || !orderItemId) return;
    setLoading(true);
    try {
      const result = await client.deleteOrderItem(selectedStore?.storeNumber, orderId.toString(), orderItemId.toString());
      if (result.success) {
        handleSnackbarOpen(t('deleteSuccess'), 'success');
        await fetchOrderDetails();
      } else {
        handleSnackbarOpen(t('deleteItemFail'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('deleteItemFail') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (itemId: number, isChecked: boolean) => {
    if (isChecked) {
      setSelectedItemIds((prev) => [...prev, itemId]);
    } else {
      setSelectedItemIds((prev) => prev.filter((id) => id !== itemId));
    }
  };

  const handleDeleteSelectedItems = () => {
    if (!orderDetails || selectedItemIds.length === 0) return;
    const selectedSKUs = orderDetails.items
      .filter(item => selectedItemIds.includes(item.id))
      .map(item => item.sku)
      .join(', ');
    setDialogState({
      open: true,
      title: t('deleteModalTitle'),
      message: `${t('deleteMultiModalMessage')}: ${selectedSKUs}?`,
      confirmAction: () => handleConfirmMultipleDeletion(),
    });
  };

  const handleConfirmMultipleDeletion = async () => {
    if (!orderDetails) return;
    setLoading(true);
    try {
      const request: DeleteOrderItemsRequest = {
        orderId: orderDetails.id,
        items: selectedItemIds,
      };
      const result = await client.deleteOrderItems(selectedStore?.storeNumber, request);
      if (result.success) {
        handleSnackbarOpen(t('multipleDeleteSuccess'), 'success');
        setSelectedItemIds([]);
        await fetchOrderDetails();
      } else {
        handleSnackbarOpen(t('multipleDeleteError'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('multipleDeleteError') + ' ' + error, 'error');
    } finally {
      setLoading(false);
      setDialogState(prev => ({ ...prev, open: false }));
    }
  };

  const handleConfirmDeletion = async (itemId: number, _itemNumber: number) => {
    if (itemId !== null) {
      if (quantitiesRef.current[itemId] !== undefined) {
        const { current } = quantitiesRef;
        const newQuantities = { ...current };
        delete newQuantities[itemId];
        quantitiesRef.current = newQuantities;
      }
      if (promoQuantitiesRef.current[itemId] !== undefined) {
        const { current } = promoQuantitiesRef;
        const newQuantities = { ...current };
        delete newQuantities[itemId];
        promoQuantitiesRef.current = newQuantities;
      }
      setDialogState(prev => ({ ...prev, open: false }));
      await handleDeleteOrderItem(Number(id), itemId);
    }
  };

  const handleConfirmDeleteItemGroup = async (itemNumber: number) => {
    if (!orderDetails) {
      return;
    }

    const groupedLineIds = orderDetails.items
      .filter(item => item.itemId === itemNumber)
      .map(item => item.id);

    if (groupedLineIds.length === 0) {
      setDialogState(prev => ({ ...prev, open: false }));
      return;
    }

    setLoading(true);
    try {
      const request: DeleteOrderItemsRequest = {
        orderId: orderDetails.id,
        items: groupedLineIds,
      };

      const result = await client.deleteOrderItems(selectedStore?.storeNumber, request);
      if (result.success) {
        handleSnackbarOpen(t('multipleDeleteSuccess'), 'success');
        await fetchOrderDetails();
      } else {
        handleSnackbarOpen(t('multipleDeleteError'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('multipleDeleteError') + ' ' + error, 'error');
    } finally {
      setLoading(false);
      setDialogState(prev => ({ ...prev, open: false }));
    }
  };

  const handleOpenDialogForDeletion = (_itemId: number, itemNumber: number) => {
    setDialogState({
      open: true,
      title: t('deleteModalTitle'),
      message: t('deleteModalMessage'),
      confirmAction: () => handleConfirmDeleteItemGroup(itemNumber),
    });
  };

  const handleConfirmAction = async () => {
    if (dialogState.confirmAction) {
      await dialogState.confirmAction();
    }
    setDialogState(prev => ({ ...prev, open: false }));
  };

  const handleCancelDialog = () => {
    setDialogState(prev => ({ ...prev, open: false }));
  };

  const clearFilters = () => {
    setProductSearch('');
    setCategory(-1);
    if (orderDetails) {
      setFilteredItems(orderDetails.items);
    }
  };

  const handleOrderCopyConfirm = async (orderId: number) => {
    setIsCopyOrderModalOpen(false);
    setLoading(true);
    try {
      const result = await client.copyOrder(selectedStore?.storeNumber, orderDetails?.id.toString(), orderId.toString());
      if (result.success) {
        handleSnackbarOpen(t('copySuccess', 'Order copied successfully'), 'success');
        await fetchOrderDetails();
      } else {
        handleSnackbarOpen(t('copyError', 'Failed to copy order'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('copyError', 'Failed to copy order') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialogForApproveOrder = () => {
    if (!ensureCustomerPoNumberIsValid()) {
      return;
    }

    setCheckoutDialogData({
      title: t('orderConfirmationTitle', 'Order Confirmation'),
      message: t('checkoutSubText'),
      confirmAction: () => handleApproveOrder(),
    });
    setIsCheckoutDialogOpen(true);
  };

  const handleApproveOrder = async () => {
    if (!selectedStore || !orderDetails) {
      handleSnackbarOpen(t('approveError'), 'error');
      return;
    }
    setLoading(true);
    try {
      let result;
      if (orderDetails.status === OrderStatus.Amended) {
        result = await client.approveAmendment(selectedStore.storeNumber, orderDetails.id.toString());
      } else {
        result = await client.approveOrder(selectedStore.storeNumber, orderDetails.id.toString());
      }
      if (result.success) {
        handleSnackbarOpen(t('approveSuccess'), 'success');
        await fetchOrderDetails();
      } else {
        handleSnackbarOpen(t('approveFail'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('approveError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectButtonClick = () => {
    setRejectDialogOpen(true);
  };

  const handleRejectDialogClose = () => {
    setRejectDialogOpen(false);
  };

  const handleRejectOrder = async () => {
    setRejectDialogOpen(false);
    if (!selectedStore || !orderDetails) {
      handleSnackbarOpen(t('rejectError'), 'error');
      return;
    }
    setLoading(true);
    try {
      let result;
      if (orderDetails.status === OrderStatus.Amended) {
        result = await client.rejectAmendment(selectedStore.storeNumber, orderDetails.id.toString());
      } else {
        result = await client.rejectOrder(selectedStore.storeNumber, orderDetails.id.toString());
      }
      if (result.success) {
        handleSnackbarOpen(t('rejectSuccess'), 'success');
        setActiveOrder(null);
        setActiveContainerData(null);
      } else {
        handleSnackbarOpen(t('rejectFail'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('rejectError'), 'error');
    } finally {
      setLoading(false);
      navigate('/orders');
    }
  };

  const handleExitAndSave = async () => {
    setIsExitAndSaveClicked(true);
    if (!loading) {
      if (orderDetails?.status === OrderStatus.Pending) {
        await autosaveItem();
      }
      navigate('/orders');
    }
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDeleteOrderDialog = () => {
    setDialogState({
      open: true,
      title: t('deleteOrderModalTitle', 'Delete Order'),
      message: t('deleteOrderModalMessage', 'Are you sure you want to delete this order? This action cannot be undone.'),
      confirmAction: handleConfirmDeleteOrder,
    });
  };

  const handleConfirmDeleteOrder = async () => {
    if (!selectedStore || !orderDetails?.id) {
      handleSnackbarOpen(t('deleteOrderError', 'Could not delete order. Store or Order ID missing.'), 'error');
      return;
    }
    setLoading(true);
    try {
      const result = await client.deleteOrder(selectedStore.storeNumber, orderDetails.id.toString());
      if (result.success) {
        handleSnackbarOpen(t('deleteOrderSuccess', 'Order deleted successfully.'), 'success');
        setActiveOrder(null);
        setActiveContainerData(null);
        navigate('/orders');
      } else {
        handleSnackbarOpen(result.message || t('deleteOrderFail', 'Failed to delete order.'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('deleteOrderError', 'An error occurred while deleting the order.') + ' ' + error, 'error');
    } finally {
      setLoading(false);
      setDialogState(prev => ({ ...prev, open: false }));
    }
  };

  const handleDownloadSpreadsheet = async () => {
    if (!selectedStore || !orderDetails?.id) {
      handleSnackbarOpen(t('downloadSpreadsheetError', 'Could not download spreadsheet. Store or Order ID missing.'), 'error');
      return;
    }

    setIsDownloadingSpreadsheet(true);
    try {
      const result = await client.downloadOrderSpreadsheet(selectedStore.storeNumber, orderDetails.id.toString());
      if (result.success && result.data) {
        // Decode base64 content and create blob
        const binaryString = atob(result.data.fileData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        exportFile(blob, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', result.data.fileName);
        handleSnackbarOpen(t('downloadSpreadsheetSuccess'), 'success');
      } else {
        handleSnackbarOpen(result.message || t('downloadSpreadsheetError'), 'error');
      }
    } catch (error) {
      console.error('Failed to download spreadsheet:', error);
      handleSnackbarOpen(t('downloadSpreadsheetError'), 'error');
    } finally {
      setIsDownloadingSpreadsheet(false);
    }
  };

  const handleUploadSpreadsheet = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedStore || !orderDetails?.id) {
      if (!file) {
        handleSnackbarOpen(t('selectFile', 'Please select a file.'), 'error');
      } else {
        handleSnackbarOpen(t('uploadSpreadsheetError', 'Could not upload spreadsheet. Store or Order ID missing.'), 'error');
      }
      return;
    }

    setIsUploadingSpreadsheet(true);
    try {
      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const request = {
        fileName: file.name,
        fileData: base64Content,
      };

      const result = await client.uploadOrderSpreadsheet(selectedStore.storeNumber, orderDetails.id.toString(), request);
      if (result.success) {
        handleSnackbarOpen(t('uploadSpreadsheetSuccess'), 'success');
      }
    } 
    catch (error) 
    {
      console.error('Failed to upload spreadsheet:', error);
    } 
    finally 
    {
      await fetchOrderDetails();
      setIsUploadingSpreadsheet(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const updateShippingDetailsContextForActiveOrder = (method: string, containerCharge: string, totalCharge: string) => {
    setShippingDetails({
      deliveryMethod: method,
      containerCharge: containerCharge,
      totalDeliveryCharge: totalCharge,
    });
  };

  useEffect(() => {
    const fetchShippingDetails = async () => {
      try {

        const result = await client.getShippingDetails(
          selectedStore?.storeNumber ?? '',
          orderDetails?.id?.toString() ?? '',
          selectedShippingMethodId ?? '',
        );

        if (result.success && result.data) {
          const data = result.data;
          const containerChargeValue = Number(data.deliveryCharge) || 0;
          const chargeTotalValue = Number(data.totalDeliveryCharge) || 0;
          if (data.isDisabled) {
            setDeliveryMethod('');
            setSelectedShippingMethodId('');
            setContainerCharge('0');
            setDeliveryChargeTotal('0');
          } else {
            setDeliveryMethod(data.deliveryMethod);
            setContainerCharge(containerChargeValue.toString());
            setDeliveryChargeTotal(chargeTotalValue.toString());

            if (data.deliveryMethodId) {
              setSelectedShippingMethodId(data.deliveryMethodId);
            } else if (data.deliveryOptions && data.deliveryOptions.length > 0) {

              const matchingOption = data.deliveryOptions.find(option =>
                option.name === data.deliveryMethod,
              );
              if (matchingOption) {
                setSelectedShippingMethodId(matchingOption.shippingMethodId.toString());
              }
            }
          }

          setCountry(data.country);
          setLocation(data.location);
          setContainerType(data.containerType ?? '');
          setPalletType(data.palletType ?? '');
          setDeliveryOptions(data.deliveryOptions || []);

          setShippingDetails({
            deliveryMethod: data.deliveryMethod,
            containerCharge: containerChargeValue.toFixed(2),
            totalDeliveryCharge: chargeTotalValue.toFixed(2),
          });
        }

      } catch (error) {
        console.error('Failed to fetch shipping details', error);
      }
    };

    if (selectedStore?.storeNumber && orderDetails?.id) {
      fetchShippingDetails();
    }
  }, [selectedStore?.storeNumber, orderDetails?.id]);


  useEffect(() => {
    const showShippingWarning = async () => {
      if (tabValue !== OrderDetailsEnum.Shipping) return;

      try {
        const result = await client.getShippingDetails(
          selectedStore?.storeNumber ?? '',
          orderDetails?.id?.toString() ?? '',
          selectedShippingMethodId ?? '',
        );

        if (result.success && result.data && result.data.isDisabled) {
          handleSnackbarOpen('Please select an active delivery method.', 'warning');
        }
      } catch (error) {
        console.error('Failed to check shipping status for tab', error);
      }
    };

    if (selectedStore?.storeNumber && orderDetails?.id && tabValue === OrderDetailsEnum.Shipping) {
      showShippingWarning();
    }
  }, [tabValue, selectedStore?.storeNumber, orderDetails?.id]);

  const autocompleteOptions = [{ id: -1, name: 'All', parentName: '' }, ...(Array.isArray(categories) ? categories : [])];
  const quantityError = (item: OrderItemDetails): string | null => {
    const quantity = quantitiesRef.current[item.id] ?? item.quantity;

    if (item.id === 0) return null;

    if (quantity < 0) {
      return t('negativeQuantityNotAllowed', 'Negative quantity is not allowed');
    }

    if ((item.maxQuantity != null) && quantity > item.maxQuantity)
      return `${t('maxQuantityAllowedIs')}: ${item.maxQuantity}`;

    if ((item.minQuantity != null) && quantity < item.minQuantity)
      return `${t('minQuantityAllowedIs')}: ${item.minQuantity}`;

    return null;
  };

  const isQuantityError = (item: OrderItemDetails): boolean => {
    return quantityError(item) != null;
  };

  const anyErrors = (): boolean => {
    return filteredItems.some(x => isQuantityError(x));
  };

  const handleOpenDialogForCheckoutOrder = (orderId: number) => {
    setCheckoutDialogData({
      title: t('orderConfirmationTitle', 'Order Confirmation'),
      message: t('checkoutSubText'),
      confirmAction: () => onCheckoutOrder(orderId),
    });
    setIsCheckoutDialogOpen(true);
  };

  const handleOpenDialogForRejectOrder = () => {
    setCheckoutDialogData({
      title: t('rejectOrderTitle', 'Reject Order'),
      message: t('rejectSubText'),
      confirmAction: () => handleRejectOrder(),
    });
    setIsCheckoutDialogOpen(true);
  };

  // Role ID 222 = OrderCheckoutNotification
  const ORDER_CHECKOUT_NOTIFICATION_ROLE = 222 as Roles;

  const onCheckoutOrder = async (orderId: number) => {
    try {
      setLoading(true);
      
      let notificationEmails: string[] = [];
      try {
        const usersResponse = await getStoreUsersWithRole(ORDER_CHECKOUT_NOTIFICATION_ROLE, selectedStore?.storeNumber ?? '');
        if (usersResponse?.data) {
          notificationEmails = usersResponse.data
            .filter(user => user.emailAddress)
            .map(user => user.emailAddress);
        }
      } catch (error) { 
        logError(error);
      }
      
      const response = await client.checkoutOrder(selectedStore?.storeNumber, orderId.toString(), notificationEmails);
      if (response.success) {
        handleSnackbarOpen(t('orderCheckedOut'), 'success');
        modifiedItemsRef.current = new Set();
        quantitiesRef.current = {};
        promoQuantitiesRef.current = {};
        clearActiveOrder();
        navigate('/orders', { replace: true });

        try {
          await addTasks({
            storeNumber: selectedStore?.storeNumber ?? '',
            taskType: TaskType.OrderCheckout,
            description: `New Order #${orderId}`,
            resourceId: orderId,
          });
        } catch (taskError) {
          logError('Failed to create order review task: ' + taskError);
        }
      }
      else {
        setWarningMessage(response.message);
      }
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
    }

  };

  const handleDeliveryDateChange = async (date: Dayjs | null) => {
    if (!date || !orderDetails?.id) {
      return;
    }

    const minDate = getMinimumDeliveryDate();
    if (date.startOf('day').isBefore(minDate)) {
      setSelectedDeliveryDate(dayjs(orderDetails.deliveryDate, 'DD-MM-YYYY'));
      handleSnackbarOpen(
        t('deliveryDateBeforeMinimum', 'Delivery date must be on or after {{date}}', {
          date: minDate.format('DD/MM/YYYY'),
        }),
        'error',
      );
      return;
    }

    setSelectedDeliveryDate(date);
    try {
      const apiDateFormat = date.format('DD-MM-YYYY');
      const result = await client.updateOrderDeliveryDate(
        selectedStore?.storeNumber,
        orderDetails.id.toString(),
        apiDateFormat,
      );
      if (result.success) {
        handleSnackbarOpen(t('deliveryDateUpdated', 'Delivery date updated'), 'success');
        await fetchOrderDetails();
      } else {
        setSelectedDeliveryDate(dayjs(orderDetails.deliveryDate, 'DD-MM-YYYY'));
        handleSnackbarOpen(result.message || t('deliveryDateUpdateFailed', 'Failed to update delivery date'), 'error');
      }
    } catch (error) {
      setSelectedDeliveryDate(dayjs(orderDetails.deliveryDate, 'DD-MM-YYYY'));
      handleSnackbarOpen(t('deliveryDateUpdateFailed', 'Failed to update delivery date') + ': ' + error, 'error');
    }
  };

  const handleCustomerPoNumberBlur = async () => {
    if (!orderDetails?.id) {
      return;
    }

    const trimmedValue = customerPoNumber.trim();
    const currentValue = (orderDetails.customerPoNumber ?? '').trim();

    if (isCustomerPoNumberRequired() && trimmedValue === '') {
      setCustomerPoNumberError(true);
      handleSnackbarOpen(t('customerPoNumberRequired'), 'error');
      return;
    }

    setCustomerPoNumberError(false);

    if (trimmedValue === currentValue) {
      return;
    }

    if (trimmedValue !== '' && !/^[a-zA-Z0-9 \-_/.,#&()]+$/.test(trimmedValue)) {
      handleSnackbarOpen(t('customerPoNumberInvalid'), 'error');
      setCustomerPoNumber(currentValue);
      return;
    }

    try {
      const result = await client.updateOrderCustomerPoNumber(
        selectedStore?.storeNumber,
        orderDetails.id.toString(),
        trimmedValue,
      );
      if (result.success) {
        handleSnackbarOpen(t('customerPoNumberUpdated'), 'success');
        await fetchOrderDetails();
      } else {
        handleSnackbarOpen(result.message || t('customerPoNumberUpdateFailed'), 'error');
        setCustomerPoNumber(currentValue);
      }
    } catch (error) {
      handleSnackbarOpen(t('customerPoNumberUpdateFailed') + ': ' + error, 'error');
      setCustomerPoNumber(currentValue);
    }
  };

  const getQuantity = (webOrderItemId: number, fallback: number) => {
    const fromQtyRef = quantitiesRef.current[webOrderItemId];
    if (fromQtyRef !== undefined) return fromQtyRef;

    const fromOrderRef =
      orderDetailsForTableRef.current?.items.find(i => i.id === webOrderItemId)?.quantity;
    if (fromOrderRef !== undefined) return fromOrderRef;

    return fallback;
  };

  const getPromoQuantity = (webOrderItemId: number, fallback: number) => {
    const fromQtyRef = promoQuantitiesRef.current[webOrderItemId];
    if (fromQtyRef !== undefined) return fromQtyRef;

    const fromOrderRef =
      orderDetailsForTableRef.current?.items.find(i => i.id === webOrderItemId)?.quantity;
    if (fromOrderRef !== undefined) return fromOrderRef;

    return fallback;
  };

  const setQuantityInRef = (webOrderItemId: number, value: number) => {
    quantitiesRef.current[webOrderItemId] = value;

    if (!modifiedItemsRef.current.has(webOrderItemId)) {
      const next = new Set(modifiedItemsRef.current);
      next.add(webOrderItemId);
      modifiedItemsRef.current = next;
    }
  };

  const setPromoQuantityInRef = (webOrderItemId: number, value: number) => {
    promoQuantitiesRef.current[webOrderItemId] = value;

    if (!modifiedItemsRef.current.has(webOrderItemId)) {
      const next = new Set(modifiedItemsRef.current);
      next.add(webOrderItemId);
      modifiedItemsRef.current = next;
    }
  };

  const handleCheckoutClick = useCallback(() => {
    if (!ensureCustomerPoNumberIsValid()) {
      return;
    }

    handleOpenDialogForCheckoutOrder(orderDetails?.id ?? 0);
  }, [orderDetails?.id, customerPoNumber, orderDetails?.requireCustomerPoNumber]);

  const handleProductSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setProductSearch(e.target.value);
  }, []);

  const handleCategoryChange = useCallback((event: React.SyntheticEvent, newValue: SimpleCategory | null) => {
    setCategory(newValue ? newValue.id : -1);
  }, []);

  const handleBrowseCatalogueClick = useCallback(() => {
    navigate(`/products-list/${orderDetails?.id}`);
  }, [navigate, orderDetails?.id]);

  const handleClearBasketClick = useCallback(() => {
    handleOpenDialogForClearBasket(orderDetails?.id ?? 0);
  }, [orderDetails?.id]);

  const handleRejectClick = useCallback(() => {
    handleRejectButtonClick();
  }, [orderDetails?.id]);

  const handleApproveClick = useCallback(() => {
    if (!ensureCustomerPoNumberIsValid()) {
      return;
    }

    handleApproveOrder();
  }, [orderDetails?.id, customerPoNumber, orderDetails?.requireCustomerPoNumber]);

  const handleRejectAmendedClick = useCallback(() => {
    handleOpenDialogForRejectOrder();
  }, [orderDetails?.id]);

  const handleApproveAmendedClick = useCallback(() => {
    handleOpenDialogForApproveOrder();
  }, [orderDetails?.id]);

  return (
    <PageArea>

      <input aria-hidden
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".xlsx,.xls,.csv"
        onChange={handleFileUpload}
      />

      <Grid item
        xs={12}
        mb={10}
        sx={{ textAlign: 'left' }}>
        <BackButton
          handleClick={handleExitAndSave}
          title={t('exitAndSave')}
          isLink={false}
        />
      </Grid>

      <Grid container>
        <Grid item
          container
          xs={12}>          <Grid item
            container
            alignItems={'end'}
            justifyContent="space-between">
            <Grid item
              xs={12}
              md="auto">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <PageTitle>{t('order')} {id}</PageTitle>
                {orderDetails && <OrderStatusChip orderStatus={orderDetails.status} />}
              </Box>
            </Grid>
            <Grid item
              xs={12}
              md="auto"
              sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'stretch', md: 'flex-end' } }}
            >
              {canDeleteOrder() && (
                <Button
                  variant="bad"
                  disabled={loading}
                  size="lg"
                  onClick={handleOpenDeleteOrderDialog}
                  startIcon={<DeleteIcon />}
                  sx={{ minWidth: '250px', maxWidth: '250px' }}
                >
                  {t('deleteOrder', 'Delete Order')}
                </Button>
              )}
              {canCheckoutOrder() && (
                <Button
                  variant="primary"
                  disabled={
                    loading || filteredItems.length === 0 || anyErrors() || !hasValidCustomerPoNumber()
                  }
                  size="lg"
                  onClick={handleCheckoutClick}
                  sx={{ minWidth: '250px', maxWidth: '250px' }}
                >
                  {t('checkoutOrder')}
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item
          container
          spacing={3}
          alignItems="center"
          xs={12}
          p={3}>
          <Grid item
            xl={3}
            lg={3}
            md={4}
            sm={6}
            xs={12}>
            <TextField
              fullWidth
              size="small"
              label={t('orderType')}
              disabled
              value={t(OrderTypeTranslationMap[orderType as keyof typeof OrderTypeTranslationMap]) || orderType}
            />
          </Grid>
          {displayDeliveryDate() && (
            <Grid item
              xl={3}
              lg={3}
              md={4}
              sm={6}
              xs={12}>
              {orderDetails?.amendmentDetails && orderDetails?.status === 'Amended' ? (
                <Tooltip
                  title={orderDetails.amendmentDetails.replace(/(\d{2}\/\d{2}\/\d{4})\s+\d{2}:\d{2}:\d{2}/g, '$1')}
                  arrow
                  placement="top"
                >
                  <div>
                    <DatePickerLocalized
                      sx={{
                        backgroundColor: '#FFFAEB',
                      }}
                      label={t('deliveryDate')}
                      valueForDateCalendar={selectedDeliveryDate}
                      useCalendar={false}
                      format="DD/MM/YYYY"
                      disabled={!canAmendOrderDate()}
                      onChangeDateCalendar={handleDeliveryDateChange}
                      minDate={getMinimumDeliveryDate()}
                    />
                  </div>
                </Tooltip>
              ) : (
                <DatePickerLocalized
                  label={t('deliveryDate')}
                  valueForDateCalendar={selectedDeliveryDate}
                  useCalendar={false}
                  format="DD/MM/YYYY"
                  disabled={!canAmendOrderDate()}
                  onChangeDateCalendar={handleDeliveryDateChange}
                  minDate={getMinimumDeliveryDate()}
                />
              )}
            </Grid>
          )}
          {displayCustomerPoNumber() && (
            <Grid item
              xl={3}
              lg={3}
              md={4}
              sm={6}
              xs={12}>
              <TextField
                fullWidth
                size="small"
                required={isCustomerPoNumberRequired()}
                label={t('customerPoNumber')}
                value={customerPoNumber}
                disabled={!canAmendCustomerPoNumber()}
                error={customerPoNumberError}
                helperText={customerPoNumberError ? t('customerPoNumberRequired') : undefined}
                onChange={(event) => {
                  setCustomerPoNumber(event.target.value);
                  if (customerPoNumberError && event.target.value.trim().length > 0) {
                    setCustomerPoNumberError(false);
                  }
                }}
                onBlur={handleCustomerPoNumberBlur}
                inputProps={{ maxLength: 35 }}
              />
            </Grid>
          )}
          {(orderDetails?.status === OrderStatus.Pending || orderDetails?.status === 'Review') && (
            <Grid item
              xs
              sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}
            >
              <Tooltip title={t('downloadSpreadsheetTooltip')}>
                <Button
                  variant="secondary"
                  disabled={loading || isDownloadingSpreadsheet}
                  size="lg"
                  onClick={handleDownloadSpreadsheet}
                  startIcon={<DownloadIcon />}
                  sx={{ minWidth: '120px' }}
                >
                  {isDownloadingSpreadsheet ? t('loading', 'Loading...') : t('downloadSpreadsheet')}
                </Button>
              </Tooltip>
              <Tooltip title={t('uploadSpreadsheetTooltip')}>
                <Button
                  variant="secondary"
                  disabled={loading || isUploadingSpreadsheet || !canEditOrder()}
                  size="lg"
                  onClick={handleUploadSpreadsheet}
                  startIcon={<UploadIcon />}
                  sx={{ minWidth: '120px' }}
                >
                  {isUploadingSpreadsheet ? t('loading', 'Loading...') : t('uploadSpreadsheet')}
                </Button>
              </Tooltip>
            </Grid>
          )}
        </Grid>
        <Grid item
          container
          xs={12}
          mt={5}
          sx={{
            border: '1px solid',
            borderColor: theme.palette.custom.gray[300],
          }}>
          <Grid container
            sx={{
              padding: '20px',
            }}
            spacing={2}>
            <Grid item
              xl={12}
              md={12}
              xs={12}
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              sx={{ padding: 0 }}
            >
              <Grid container
                spacing={2}>
                <Grid item
                  xl={9}
                  md={12}
                  xs={12}>
                  <Box>
                    <Grid item>
                      <ContainerPlanningView containerData={containerData}
                        currencyCode={currencyCode ?? ''}
                        deliveryChargeTotal={deliveryChargeTotal} />
                    </Grid>
                  </Box>
                </Grid>
                <Grid item
                  xl={3}
                  md={12}
                  xs={12}>
                  <Box>
                    <Grid container
                      spacing={2}>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid sx={{ mt: 5, mb: 1, px: 2 }}>
              <Typography
                color="textSecondary"
                sx={{ fontSize: '12px', fontStyle: 'italic', lineHeight: 1.5 }}>
                {t('subjectToChange')}
              </Typography>
            </Grid>
          </Grid>
          <Grid item
            xs={12}>
            <Divider />
          </Grid>

          <Tabs value={tabValue}
            onChange={handleChange}>
            <Tab label={t('basket')}
              value={OrderDetailsEnum.Basket} />
            <Tab label={t('shipping')}
              value={OrderDetailsEnum.Shipping} />
            <Tab label={t('documents')}
              value={OrderDetailsEnum.Documents} />
          </Tabs>

          {tabValue === OrderDetailsEnum.Basket && (
            <Grid container>
              <Grid item
                container
                sx={{
                  px: 4,
                  py: 6,
                }}
                xs={12}
                spacing={5}>
                <Grid item
                  xs={12}
                  md={3}>
                  <TextField
                    fullWidth
                    size='small'
                    placeholder={t('searchByItemNameOrSKU')}
                    value={productSearch}
                    onChange={handleProductSearchChange}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SvgIcon><SearchSm /></SvgIcon>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  md={3}>
                  <Autocomplete
                    size='small'
                    options={autocompleteOptions}
                    getOptionLabel={(option) => isOrderCategory(option.name) ? t(OrderCategoriesTranslationMap[option.name as keyof typeof OrderCategoriesTranslationMap]) : option.name}
                    value={categories.find(cat => cat.id === category) ?? autocompleteOptions[0]}
                    onChange={handleCategoryChange}
                    renderInput={(params) => (
                      <TextField {...params}
                        label={t('category')}
                        placeholder={t('choose')} />
                    )}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  md={2}>
                  <Button
                    variant="bad"
                    disabled={loading || selectedItemIds.length === 0 || !canEditOrder()}
                    size="lg"
                    fullWidth
                    onClick={handleDeleteSelectedItems}
                    startIcon={<DeleteIcon />}
                  >
                    {t('deleteSelected')}
                  </Button>
                </Grid>
                <Grid item
                  xs={12}
                  md={2}>
                  <Button fullWidth
                    variant="secondary"
                    disabled={loading}
                    onClick={clearFilters}
                    size="lg">
                    {t('clearFilters')}
                  </Button>
                </Grid>
                <Grid item
                  xs={12}
                  md={2}>
                  <Button fullWidth
                    variant="primary"
                    disabled={loading}
                    onClick={handleSearch}
                    size="lg"
                    startIcon={<SearchSm />}>
                    {t('search')}
                  </Button>
                </Grid>
              </Grid>
              <Grid item
                xs={12}>
                {(loading || isUploadingSpreadsheet) ? (
                  <Skeleton variant="rectangular"
                    width={'100%'}
                    height='max(calc(95vh + 121px), calc(400px + 121px))' />
                ) : (
                  <OrderTable
                    filteredItems={filteredItems}
                    orderDetails={orderDetailsForTableRef.current}
                    selectedItemIds={selectedItemIds}
                    handleCheckboxChange={handleCheckboxChange}
                    canDeleteOrderItem={canDeleteOrderItem}
                    canChangeOrderQuantity={canChangeOrderQuantity}
                    currencyCode={currencyCode}
                    canEditOrder={canEditOrder}
                    handleEditClick={handleEditClick}
                    isQuantityError={isQuantityError}
                    quantityError={quantityError}
                    handleConfirmDeletion={handleConfirmDeletion}
                    autosaveItem={autosaveItem}
                    handleOpenDialogForDeletion={handleOpenDialogForDeletion}
                    getQuantity={getQuantity}
                    getPromoQuantity={getPromoQuantity}
                    setQuantityInRef={setQuantityInRef}
                    setPromoQuantityInRef={setPromoQuantityInRef}
                    deliveryChargeTotal={deliveryChargeTotal} 
                    clearFilters={clearFilters}       
                    productSearch={productSearch}  
                    handleBrowseCatalogueClick={handleBrowseCatalogueClick}
                  />
                )}
              </Grid>
              <Grid item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 7, mb: 5, px: 7 }}>
                {canDeleteOrderItem() && filteredItems.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      sx={{ px: 3, py: 1.5 }}
                      onClick={handleBrowseCatalogueClick}>
                      {t('browseCatalogue')}
                    </Button>
                    <Button
                      sx={{ px: 3, py: 1.5 }}
                      disabled={loading || filteredItems.length === 0 || anyErrors()}
                      onClick={handleClearBasketClick}>
                      {t('clearBasket')}
                    </Button>
                  </Box>
                )}
                {canApproveOrder() && (
                  <Box>
                    <Button
                      variant="bad"
                      sx={{ ml: 2, px: 3, py: 1.5 }}
                      startIcon={<CancelIcon />}
                      disabled={loading || !hasPermissionTo(['OrdersApprove'])}
                      onClick={handleRejectClick}>
                      {t('reject')}
                    </Button>
                    <Button
                      variant="primary"
                      sx={{ ml: 2, px: 3, py: 1.5 }}
                      startIcon={<CheckIcon />}
                      disabled={loading || !hasPermissionTo(['OrdersApprove']) || !hasValidCustomerPoNumber()}
                      onClick={handleApproveClick}>
                      {t('approve')}
                    </Button>
                  </Box>
                )}
                {(canRejectAmendedOrder() || canApproveAmendedOrder()) && (
                  <Box sx={{ display: 'flex', gap: 2, marginLeft: 'auto' }}>
                    {canRejectAmendedOrder() && (
                      <Button
                        variant="bad"
                        sx={{ px: 3, py: 1.5 }}
                        startIcon={<CancelIcon />}
                        disabled={loading || !hasPermissionTo(['OrdersRejectConfirmation'])}
                        onClick={handleRejectAmendedClick}>
                        {t('reject')}
                      </Button>
                    )}
                    {canApproveAmendedOrder() && (
                      <Button
                        variant="primary"
                        sx={{ px: 3, py: 1.5 }}
                        startIcon={<CheckIcon />}
                        disabled={loading || !hasPermissionTo(['OrdersApproveConfirmation']) || !hasValidCustomerPoNumber()}
                        onClick={handleApproveAmendedClick}>
                        {t('approve')}
                      </Button>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          )}

          {tabValue === OrderDetailsEnum.Shipping && (

            <Grid container
              spacing={3}
              sx={{ mt: 3, ml: 3, mr: 2 }}>
              <Grid item
                xs={12}
                md={6}
                sx={{ px: { xs: 0, md: 4 } }}>
                <TextField
                  select
                  fullWidth
                  label={t('deliveryMethod')}
                  sx={{ mb: 3 }}
                  value={selectedShippingMethodId}
                  disabled={!canUpdateDeliveryMethod()}
                  onChange={async e => {
                    const newShippingMethodId = e.target.value;
                    setSelectedShippingMethodId(newShippingMethodId);

                    const selectedOption = deliveryOptions.find(option =>
                      option.shippingMethodId.toString() === newShippingMethodId,
                    );

                    if (selectedOption) {
                      setDeliveryMethod(selectedOption.name);
                      let totalCharge: number;
                      let displayCharge: number;

                      // Try pricing engine
                      if (containerData && containerData.totalPalletsNeeded > 0) {
                        const engineResult = await resolveDeliveryCharge(
                          Number(selectedOption.shippingMethodId),
                          containerData.totalPalletsNeeded,
                          containerData.containerFillPercents,
                          orderDetails?.deliveryDate ?? new Date().toISOString().split('T')[0],
                          selectedOption.charge,
                        );
                        if (engineResult) {
                          // Engine returns total order charge — derive per-container display value
                          totalCharge = engineResult.totalCharge ?? 0;
                          const count = computeContainerCount(containerData);
                          displayCharge = count > 0 ? totalCharge / count : totalCharge;
                          setIsAutomatedPricingActive(true);
                        } else {
                          // Fallback: flat rate × container count
                          displayCharge = selectedOption.charge;
                          totalCharge = displayCharge * computeContainerCount(containerData);
                          setIsAutomatedPricingActive(false);
                        }
                      } else {
                        displayCharge = selectedOption.charge;
                        totalCharge = displayCharge * computeContainerCount(containerData);
                        setIsAutomatedPricingActive(false);
                      }

                      setContainerCharge(displayCharge.toString());
                      setDeliveryChargeTotal(totalCharge.toString());
                      updateShippingDetailsContextForActiveOrder(selectedOption.name, displayCharge.toString(), totalCharge.toString());
                      autosaveShippingDetails(selectedOption.shippingMethodId, totalCharge, undefined, undefined, displayCharge);
                    }
                  }}
                >
                  {deliveryOptions.map((option) => (
                    <MenuItem key={option.shippingMethodId}
                      value={option.shippingMethodId.toString()}>
                      {option.name} ({(resolvedDeliveryOptionCharges.get(Number(option.shippingMethodId)) ?? option.charge ?? 0).toFixed(2)})
                    </MenuItem>
                  ))}
                </TextField>

              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  label={t('deliveryCharge')}
                  value={containerCharge || '0'}
                  onChange={e => {
                    const val = e.target.value;
                    if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                      setContainerCharge(val);
                    }
                  }}
                  onBlur={e => {
                    const chargeValue = parseFloat(e.target.value) || 0;
                    const containerCount = computeContainerCount(containerData);
                    const total = chargeValue * containerCount;
                    setDeliveryChargeTotal(total.toString());
                    updateShippingDetailsContextForActiveOrder(deliveryMethod, chargeValue.toString(), total.toString());
                    autosaveShippingDetails(selectedShippingMethodId, total, undefined, undefined, chargeValue);
                  }}
                  disabled={!canUpdateDeliveryCharge() || isAutomatedPricingActive}
                  sx={{ mb: 3 }}
                  inputProps={{
                    min: 0,
                    inputMode: 'decimal',
                  }}
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}
                sx={{ px: { xs: 0, md: 4 } }}>
                <TextField
                  fullWidth
                  label={t('country')}
                  sx={{ mb: 3 }}
                  value={country}
                  disabled={deliveryMethod === 'Ex Works' || loading || !canEditOrder()}
                  onChange={e => {
                    setCountry(e.target.value);
                  }}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              <Grid item
                xs={12}
                sx={{ px: { xs: 0, md: 4 } }}>
                <TextField
                  fullWidth
                  label={t('location')}
                  multiline
                  rows={3}
                  sx={{ mb: 3 }}
                  value={location}
                  disabled={deliveryMethod === 'Ex Works' || loading || !canEditOrder()}
                  onChange={e => {
                    setLocation(e.target.value);
                  }}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              <Grid item
                xs={12}
                md={6}
                sx={{ px: { xs: 0, md: 4 } }}>
                <TextField
                  select
                  fullWidth
                  label={t('container')}
                  sx={{ mb: 3 }}
                  value={containerType}
                  disabled={loading || !canChangeContainer()}
                  onChange={e => {
                    const newValue = e.target.value;
                    setContainerType(newValue);
                    const containerCount = computeContainerCount(containerData);
                    const total = parseFloat(containerCharge || '0') * containerCount;
                    autosaveShippingDetails(undefined, total, newValue);
                  }}
                >
                  {containerSizes.map((option) => (
                    <MenuItem key={option.key}
                      value={option.key}>
                      {option.value}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item
                xs={12}
                md={6}
                sx={{ px: { xs: 0, md: 4 } }}>
                <TextField
                  select
                  fullWidth
                  label={t('pallet')}
                  disabled={loading || !canChangePalletType()}
                  sx={{ mb: 3 }}
                  value={palletType}
                  onChange={e => {
                    const newValue = e.target.value;
                    setPalletType(newValue);
                    const containerCount = computeContainerCount(containerData);
                    const total = parseFloat(containerCharge || '0') * containerCount;
                    autosaveShippingDetails(undefined, total, undefined, newValue);
                  }}
                >
                  <MenuItem value={1}>UK</MenuItem>
                  <MenuItem value={2}>EU</MenuItem>
                </TextField>
              </Grid>
            </Grid>

          )}

          {tabValue === OrderDetailsEnum.Documents &&
            <Grid item
              xs={12}
              sx={{ px: { xs: 0, md: 4 }, mt: 3 }}>

              <OrderDocuments
                products={filteredItems}
                productDocuments={productDocuments}
                orderDocuments={orderDetails?.documents}
                orderStatus={orderDetails?.status}
                download={(documentId: number) => downloadFile(documentId, downloadProductDocument)}
                loadData={loadData}
              />
            </Grid>
          }
        </Grid>
      </Grid>
      <QuantityWarningDialog
        setShowQuantityWarningDialog={setShowQuantityWarningDialog}
        message={warningMessage}
        showDialog={showQuantityWarningDialog} />
      <ActionConfirmationDialog
        open={isCheckoutDialogOpen}
        icon={<WarningAmberIcon />}
        variant="warning"
        title={checkoutDialogData.title}
        message={checkoutDialogData.message}
        confirmButtonText={t('checkoutConfirm')}
        cancelButtonText={t('checkoutCancel')}
        onConfirm={() => {
          checkoutDialogData.confirmAction();
          setIsCheckoutDialogOpen(false);
        }}
        onCancel={() => setIsCheckoutDialogOpen(false)}
      />
      <ConfirmationDialog
        open={dialogState.open}
        icon={<DeleteIcon />}
        title={dialogState.title}
        message={dialogState.message}
        confirmButtonText={t('confirm')}
        cancelButtonText={t('cancel')}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelDialog}
      />
      <CopyOrderModal
        currentOrderId={orderDetails?.id ?? 0}
        orderTypeId={orderDetails?.orderTypeId ?? 0}
        open={isCopyOrderModalOpen}
        onClose={() => setIsCopyOrderModalOpen(false)}
        onConfirm={handleOrderCopyConfirm}
      />
      <ItemPriceOverrideModal
        selectedItem={selectedOverrideItem}
        openModal={handleShowOverridePrice}
        onCancel={() => { setHandleShowOverridePrice(false); setSelectedOverrideItem(undefined); }}
        onConfirm={(editedItem) => updateItemPriceOverride(editedItem)}
        currencyCode={currencyCode}
      />
      <OrderAuditModal
        currentOrderId={orderDetails?.id ?? 0}
        open={isOrderAuditModalOpen}
        onClose={() => setIsOrderAuditModalOpen(false)}
      />
      <MessagesSnackbar open={snackbar.open}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        severity={snackbar.severity}
        duration={3000} />
      <Dialog
        open={rejectDialogOpen}
        onClose={handleRejectDialogClose}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>
          {t('rejectOrder')}
        </DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid item
              xs={12}>
              <Typography variant='textMD'>
                {t('rejectOrderConfirmation', 'Are you sure you want to reject this order?')}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={handleRejectDialogClose}>
            {t('cancel')}
          </Button>
          <Button
            variant="bad"
            onClick={handleRejectOrder}>
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea >
  );
};

export default OrderDetailsPageOfd;
